// kuromoji.js を非同期ロードして日本語テキストの読み（カタカナ）を推定するラッパー。
// SceneFlow の実装を踏襲。辞書ロードは重いので dicPath ごとに Promise をキャッシュする。
// kuromoji は「漢字/フレーズの読み推定」専用の遅延・任意アセット。かな直接入力では不要。

import { builder, type IpadicFeatures, type Tokenizer } from 'kuromoji'

// 同 origin の /dict/（vite プラグインが配信）から辞書を取得する。base を尊重するため
// import.meta.env.BASE_URL を前置。kuromoji が path.join で URL を組むため CDN ではなく同 origin 絶対パス。
export const DEFAULT_DIC_PATH = `${import.meta.env.BASE_URL}dict`

const cache = new Map<string, Promise<Tokenizer<IpadicFeatures>>>()

/** kuromoji トークナイザをロードする（dicPath ごとにキャッシュ、失敗時は破棄して再試行可）。 */
export function loadTokenizer(
  dicPath: string = DEFAULT_DIC_PATH,
): Promise<Tokenizer<IpadicFeatures>> {
  const cached = cache.get(dicPath)
  if (cached) return cached
  const promise = new Promise<Tokenizer<IpadicFeatures>>((resolve, reject) => {
    builder({ dicPath }).build((err, tokenizer) => {
      if (err) {
        cache.delete(dicPath)
        reject(err instanceof Error ? err : new Error(String(err)))
        return
      }
      resolve(tokenizer)
    })
  })
  cache.set(dicPath, promise)
  return promise
}

export interface ReadingToken {
  surface: string
  /** カタカナ読み。推定できなければ空文字。 */
  reading: string
}

const KATAKANA = /^[ァ-ヴー]+$/
const HIRAGANA = /[ぁ-ゟ]/

/** ひらがな→カタカナ。 */
export function toKatakana(s: string): string {
  let out = ''
  for (const ch of s) {
    const code = ch.codePointAt(0)
    out += code !== undefined && code >= 0x3041 && code <= 0x3096 ? String.fromCodePoint(code + 0x60) : ch
  }
  return out
}

/** トークンから読み（カタカナ）を取り出す。無ければ表層がかなならそれを使う。 */
function readingOf(t: IpadicFeatures): string {
  const r = t.reading
  if (r && r !== '*' && KATAKANA.test(r)) return r
  const surface = t.surface_form
  if (KATAKANA.test(surface)) return surface
  if (HIRAGANA.test(surface)) {
    const kata = toKatakana(surface)
    if (KATAKANA.test(kata)) return kata
  }
  return ''
}

/** テキストを形態素解析し、トークンごとの読み（カタカナ）を返す。 */
export async function analyzeReading(text: string, dicPath?: string): Promise<ReadingToken[]> {
  const tokenizer = await loadTokenizer(dicPath)
  const tokens = tokenizer.tokenize(text)
  return tokens.map((t) => ({ surface: t.surface_form, reading: readingOf(t) }))
}

/** 文字列がすべてかな（ひらがな/カタカナ/長音）か。true なら kuromoji 不要で読みに使える。 */
export function isAllKana(s: string): boolean {
  return s.length > 0 && /^[ぁ-ゟァ-ヴー\s]+$/.test(s)
}
